import {
 Button,
 Card,
 Checkbox,
 Form,
 Input,
 InputNumber,
 Popover,
 Spin,
 Switch,
} from 'antd'
import ListContentLayout from 'layout/ListContentLayout'
import React, { FC, useEffect, useState } from 'react'
import { TSmartAlertPost } from 'features/automation/types'
import {
 useGetSmartListingAlertQuery,
 useSmartListingStoreMutation,
} from 'features/automation/api'
import { useGlobal } from 'raducer/global'
import SmartListingItem from './SmartListingItem'
import SmartEngegementCard from '../components/SmartEngegementCard'

const initialValues: TSmartAlertPost = {
 trigger_on_views: 0,
 price_drop_alert_views: 0,
 open_house_alert_views: 0,
 viewed_sold: false,
 viewed_price_change: false,
 viewed_open_house: true,
 status: false,
 smart_listing_pause: false,
}

const SmartListingAlert: FC = () => {
 const { global } = useGlobal()
 const [smartListingStore, { isLoading }] = useSmartListingStoreMutation()
 const [smartListingStoreAll, { isLoading: isLoadingAll }] =
  useSmartListingStoreMutation()
 const [status, setStatus] = useState(false)
 const {
  data,
  refetch,
  isLoading: pgLoading,
 } = useGetSmartListingAlertQuery(null)
 const [form] = Form.useForm<TSmartAlertPost>()

 useEffect(() => {
  if (data) {
   form.setFieldsValue({
    ...initialValues,
    ...data,
   })
   setStatus(data.status === 1)
  } else {
   form.setFieldsValue(initialValues)
  }
 }, [data])

 const handleUpdateStatus = async (val: boolean) => {
  setStatus(val)
  const values = form.getFieldsValue()
  const postData: TSmartAlertPost = {
   ...values,
   status: val ? 1 : 0,
  }
  await smartListingStore(postData).unwrap()
  refetch()
 }

 const handleUpdateChanges = async () => {
  const values = await form.validateFields()
  const postData: TSmartAlertPost = {
   ...values,
   status: status ? 1 : 0,
  }
  await smartListingStoreAll(postData).unwrap()
  refetch()
 }

 return (
  <ListContentLayout title="Smart Listing Alert">
   <div className="smart-engegement-page">
    <div className="smart-engegement-page-body">
     <Spin spinning={pgLoading}>
      <Form form={form} initialValues={initialValues}>
       <div className="smart-listing-page">
        <div className="smart-listing-main-header">
         <h2>
          Smart Listing Alert{' '}
          <Popover
           content={
            <ul
             style={{
              display: 'flex',
              flexDirection: 'column',
              rowGap: '10px',
              minWidth: '300px',
              maxWidth: '590px',
              margin: 0,
              paddingLeft: '16px',
             }}
            >
             <li>
              The Smart Listing Alert automatically assigns relevant listing
              alerts (e.g., New Listings, Price Drops,
              {global.open_house && ' Open Houses'}) based on user interactions
              such as property views, favorites, and search behavior.
             </li>
             <li>
              These alerts adapt intelligently to user engagement, reducing
              frequency or pausing if interest wanes and If a user returns after
              a period of inactivity, the alerts will resume and adjust to
              reflect their latest activity.
             </li>
             <li>
              You can customize the alert triggers, manually pause or stop
              alerts for specific users, and refine the filter criteria at any
              time for a more personalized experience.
             </li>
            </ul>
           }
          >
           <Button className="ms-2" size="small" shape="circle">
            <i className="fa fa-info" />
           </Button>
          </Popover>
         </h2>
         <Switch
          disabled={isLoading}
          onChange={(v) => {
           handleUpdateStatus(v)
          }}
          checked={status}
         />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
         <SmartEngegementCard title="Smart Triggers">
          <SmartListingItem
           name="trigger_on_views"
           input={<InputNumber disabled={!status} min={0} max={50} />}
           helper="(Min: 0, Max: 50)"
           label="Assign a New Listing Alert when"
           subLabel="The number of listing views is greater than or equal to"
           infoText="Alert Frequency: Runs on every Wednesday [Once Weekly]"
          />
          <SmartListingItem
           name="price_drop_alert_views"
           input={<InputNumber disabled={!status} min={0} max={50} />}
           helper="(Min: 0, Max: 50)"
           label="Assign a Price Drop Alert when "
           subLabel="The number of listing views is greater than or equal to "
           infoText="Alert Frequency: Runs on every Tuesday & Friday [Twice Weekly]"
          />
          {global.open_house && (
           <SmartListingItem
            name="open_house_alert_views"
            input={<InputNumber disabled={!status} min={0} max={50} />}
            helper="(Min: 0, Max: 50)"
            label="Assign Open House Alert when"
            subLabel="The number of listing views is greater than or equal to"
            infoText="Alert Frequency: Runs on every Thursday [Once Weekly]"
           />
          )}
          <span style={{ fontSize: '12px' }} className="text-muted text-sm">
           Note: Enter 0 if you wish not to assign the alert
          </span>
         </SmartEngegementCard>
         <SmartEngegementCard title="Notify user when">
          {global.user_type_sold && (
           <SmartListingItem
            input={<CheckBoxCustom disabled={!status} />}
            name="viewed_sold"
            // label="Assign a new listing alert when"
            subLabel="The property viewed has been sold"
           />
          )}
          <SmartListingItem
           input={<CheckBoxCustom disabled={!status} />}
           name="viewed_price_change"
           subLabel="The property viewed  has price change (Drop / Hike) "
          />
          {global.open_house && (
           <SmartListingItem
            input={<CheckBoxCustom disabled={!status} />}
            name="viewed_open_house"
            subLabel="The property viewed  has an Open House"
           />
          )}
         </SmartEngegementCard>
         <SmartEngegementCard title="Pause the Smart Listing Alert when">
          <SmartListingItem
           input={<CheckBoxCustom disabled={!status} />}
           name="smart_listing_pause"
           subLabel={`A new alert ( New Listing / Price Drop ${
            global.open_house ? '/ Open House ' : ''
           }) assigned from the website or from the CRM`}
          />
         </SmartEngegementCard>
        </div>
       </div>
      </Form>
     </Spin>
    </div>
    {status && (
     <div className="smart-engegement-page-footer">
      <Button
       disabled={!status}
       //  color="primary"
       shape="round"
       style={{ padding: '7px 36px', height: 'unset', fontSize: '15px' }}
       loading={isLoadingAll}
       onClick={() => {
        handleUpdateChanges()
       }}
       type="primary"
      >
       Update
      </Button>
     </div>
    )}
   </div>
  </ListContentLayout>
 )
}

export default SmartListingAlert

const CheckBoxCustom: FC<{
 disabled?: boolean
 value?: boolean
 onChange?: (v?: boolean) => void
}> = ({ onChange, value, disabled }) => {
 return (
  <Checkbox
   disabled={disabled}
   onChange={({ target }) => {
    onChange?.(target.checked)
   }}
   checked={value}
   value={value}
  />
 )
}
